import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { createUseStyles, Icon, Loader, Tooltip, useClasses, useTheme } from '@Sage/hoi-poi-ui';

import { RichTextContext } from '../..';
import CustomBubbleMenu from '../CustomBubbleMenu';
import ToolbarItem from '../ToolbarItem';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Menu');

const editorFormatsMapping = {
    b: 'bold',
    i: 'italic',
    u: 'underline',
    del: 'strike',
    ol: 'orderedList',
    ul: 'bulletList',
};

const Menu = ({
    canSubmit,
    classes: classesProp,
    compactMode,
    customActions,
    editorContent,
    focused,
    handleSubmit,
    toolbar,
    toolbarStyle,
    submitTooltip,
    isBasic,
    loading,
    isSubmitDisabled,
}) => {
    const theme = useTheme();
    const classes = useClasses(useStyles, classesProp);
    const { editor, emoji, mention } = useContext(RichTextContext);

    const toolbarItems = useCallback(() => {
        const toolbarItemProps = {
            fixed: {
                className: classes.toolbarItem,
                classNameActive: classes.toolbarItemActive,
                color: theme.colors.actionMinor[500],
            },
            floating: {
                className: classNames(classes.toolbarItem, classes.floatingToolbarItem),
                classNameActive: classes.floatingToolbarItemActive,
                color: theme.colors.primary.white,
            },
        };

        const toolbarItemStyle = compactMode
            ? toolbarItemProps.fixed
            : toolbarItemProps[toolbarStyle];

        const toolbarItems = toolbar.map((item) => {
            return (
                <ToolbarItem
                    key={item.item}
                    active={editor?.isActive(editorFormatsMapping[item.item])}
                    {...item}
                    {...toolbarItemStyle}
                />
            );
        });

        if ((mention || emoji) && (toolbarStyle === 'fixed' || compactMode)) {
            toolbarItems.push(<span key="divider" className={classes.toolbarDivider}></span>);
            if (mention) {
                toolbarItems.push(
                    <ToolbarItem
                        key="mention"
                        hint={mention.tooltip}
                        item="mention"
                        {...toolbarItemStyle}
                    />,
                );
            }

            if (emoji) {
                toolbarItems.push(
                    <ToolbarItem
                        key="emoji"
                        hint={emoji.tooltip}
                        item="emoji"
                        {...toolbarItemStyle}
                    />,
                );
            }
        }

        return toolbarItems;
    }, [classes, compactMode, editor, mention, theme, toolbar, toolbarStyle, emoji]);

    const toolbarClassNames = classNames(classes.toolbar, {
        [classes.compactMode]: compactMode && !focused,
        [classes.compactModeFocused]: compactMode && (focused || !!editorContent?.text?.length),
    });

    const isSubmitEnabled = useMemo(
        () => (!!editorContent?.text?.length || canSubmit?.(editorContent)) && !isSubmitDisabled,
        [canSubmit, editorContent, isSubmitDisabled],
    );

    if (!editor) return null;

    const submitComponent = isSubmitEnabled ? (
        <Tooltip placement="top" content={submitTooltip}>
            <span className={classes.submitIcon} onClick={handleSubmit}>
                <Icon
                    name="send"
                    size="large"
                    color={isBasic ? theme.colors.primary.white : theme.colors.actionMajor[500]}
                    key="submit"
                />
            </span>
        </Tooltip>
    ) : (
        <Icon name="send" size="large" color={theme.colors.textLight.disabled} key="submit" />
    );

    switch (true) {
        case isBasic:
            if (loading)
                return (
                    <div className={classes.basicLoader}>
                        <Loader size="tiny" />
                    </div>
                );
            return (
                <div
                    className={classNames(classes.basicSubmit, {
                        [classes.basicSubmitClickable]: isSubmitEnabled,
                    })}
                >
                    {submitComponent}
                </div>
            );
        case compactMode:
            return (
                <div className={toolbarClassNames}>
                    <div className={classes.toolbarItems} onClick={(e) => e.stopPropagation()}>
                        {toolbarItems()}
                    </div>
                    {submitComponent}
                </div>
            );
        case editor && toolbarStyle === 'floating':
            return (
                <CustomBubbleMenu editor={editor} className={classes.floatingToolbar}>
                    {toolbarItems()}
                </CustomBubbleMenu>
            );
        case editor && toolbarStyle === 'fixed':
            return (
                <div className={toolbarClassNames}>
                    {toolbarItems()}
                    {customActions && (
                        <div key="customActions" className={classes.toolbarCustomActions}>
                            {customActions}
                        </div>
                    )}
                </div>
            );
        default:
            return null;
    }
};

export default Menu;
